import PassageEditor from "@features/tests/components/PassageEditor";
import { TestPassage } from "@features/tests/types/test-detail.types";
import { memo } from "react";
import { QuestionBankPassage } from "../types/question-bank.types";

interface QuestionBankPassageAdapterProps {
  passage: QuestionBankPassage;
  passageIndex: number;
  onUpdate: (passage: QuestionBankPassage) => void;
  onRemove: () => void;
  isExpanded?: boolean;
  isIndividuallyExpanded?: boolean;
  onToggleExpansion?: (passageId: string, isExpanded: boolean) => void;
  itemId?: string; // Item ID for image storage
}

/**
 * Adapter component that allows using the existing PassageEditor component
 * with QuestionBankPassage objects
 */
function QuestionBankPassageAdapter({
  passage,
  passageIndex,
  onUpdate,
  onRemove,
  isExpanded,
  isIndividuallyExpanded,
  onToggleExpansion,
  itemId,
}: QuestionBankPassageAdapterProps) {
  // Convert QuestionBankPassage to TestPassage format
  const adaptedPassage: TestPassage = {
    id: passage.id,
    title: passage.title,
    lines: passage.lines.map((line) => ({
      number: line.number,
      text: line.text,
    })),
    images: passage.images?.map((img) => ({
      id: img.url,
      storageUrl: img.url,
      caption: img.alt,
    })),
  };

  // Handle updates from the PassageEditor and convert back to QuestionBankPassage
  const handleUpdate = (updatedTestPassage: TestPassage) => {
    const updatedQuestionBankPassage: QuestionBankPassage = {
      ...passage,
      title: updatedTestPassage.title,
      lines: updatedTestPassage.lines.map((line) => ({
        number: line.number,
        text: line.text,
      })),
      images: updatedTestPassage.images?.map((img) => ({
        url: img.storageUrl || "",
        alt: img.caption || "",
      })),
      orderIndex: passage.orderIndex, // Keep the original orderIndex from the passage
    };
    onUpdate(updatedQuestionBankPassage);
  };

  return (
    <PassageEditor
      passage={adaptedPassage}
      passageIndex={passageIndex}
      onUpdate={handleUpdate}
      onRemove={onRemove}
      isExpanded={isExpanded}
      isIndividuallyExpanded={isIndividuallyExpanded}
      onToggleExpansion={onToggleExpansion}
      testId={itemId} // Use itemId as testId for image storage
    />
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(QuestionBankPassageAdapter);
